import type { CSSProperties } from "react";

import { focusRing } from "@/components/landing/cta-styles";

/**
 * Class strings for the SMB landing (components/landing/smb). Light sections
 * use the site's semantic tokens; a navy section is `darkPanel` — the site's
 * own dark theme scoped to that section (see below) — so nothing needs a
 * "dark" twin. Every class is written out literally (no interpolation):
 * Tailwind only generates what it can read verbatim from the source.
 */

export const wrap = "relative mx-auto w-full max-w-[1240px] px-5 md:px-[clamp(20px,5vw,88px)]";
export const sectionPad = "py-[clamp(100px,13vw,190px)]";

/**
 * A navy panel is the dark theme applied to one section. `.dark` is a plain
 * class selector in globals.css and the `dark:` variant is `(&:is(.dark *))`,
 * so putting it on the <section> flips every semantic token (surfaces, text,
 * borders, the base-layer heading colour) for the subtree and fires the
 * `dark:` variants inside it. The dark surface is the prototype's navy
 * (#0A1628) already. In the site's dark theme a light section looks the same.
 *
 * `bg-surface`, not `bg-background`: the shadcn aliases (`--background`,
 * `--card`, …) are assigned `var(--surface)` on `:root` and inherit that
 * resolved value, so they do not follow a scoped `.dark`. The `--surface`
 * family is set by `.dark` itself and does.
 */
export const darkPanel = "dark bg-surface text-foreground";

/** Eyebrow: the prototype's deep blue on cream, its coral on navy. */
export const eyebrow =
  "mb-5 flex items-center gap-2.5 font-mono text-xs font-medium uppercase tracking-[0.16em] text-[#006A98] before:h-px before:w-[18px] before:bg-current before:opacity-70 before:content-[''] dark:text-signal-soft";
/** Small accent text in the eyebrow's colours (case tags, read-more). */
export const accent = "text-[#006A98] dark:text-signal-soft";
/** The prototype's blue for display type (the hero's second line, "Or get an
 *  agent…", Week 5): lifted on navy so it still reads. */
export const blueDisplay = "text-[#0086BF] dark:text-[#5FC0EA]";
export const blueText = "text-[#006A98] dark:text-[#5FC0EA]";
/** The prototype's teal: the cover's over-line and contact line on navy. */
export const tealText = "text-[#2ABFAB]";

// Headings carry an explicit colour so the intent is visible; the base layer
// gives h1–h3 the same token anyway.
export const h2 =
  "max-w-[16ch] font-serif text-[clamp(2.5rem,5.2vw,5rem)] italic leading-[1.02] tracking-[-0.015em] text-balance text-foreground-strong";
export const h3 = "font-serif text-[clamp(1.75rem,2.6vw,2.5rem)] italic leading-[1.08] text-pretty text-foreground-strong";
export const lede =
  "max-w-[36em] text-[clamp(1.19rem,1.45vw,1.44rem)] leading-[1.5] text-pretty text-foreground-muted";

export const ghostButton = `inline-flex items-center justify-center gap-2.5 rounded-none border border-border-strong px-7 py-4 text-[1.02rem] font-semibold text-foreground transition hover:-translate-y-0.5 hover:border-foreground-strong motion-reduce:hover:translate-y-0 ${focusRing}`;

/** Scroll-reveal: the element rises in once its Section is in view (the
 *  `.sq-rv` / `.sq-in` pair in globals.css), after `ms`. */
export const reveal = "sq-rv";
export const revealAfter = (ms: number): CSSProperties => ({ transitionDelay: `${ms}ms` });
